import React, { useMemo, useState } from "react";
import PortalCard from "../components/PortalCard";
import IdentityChip from "../components/IdentityChip";

interface IdentityChipProps {
  label: string;
  onLogout: () => void;
  onSwitchUser: () => void;
}

interface PortalChooserProps {
  portals: string[];
  onSelect: (portalUid: string) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  // Opt-in client-side filter for large portal lists (e.g. support/master
  // users whose identity spans many portals). Default off so the standard
  // portal chooser is unchanged. Mirrors CompanyChooser's filterable prop
  // (ZC-5658). ZC-5705.
  filterable?: boolean;
  /**
   * ZC-5707: When present, renders an IdentityChip in the header showing who
   * the user is signed in as (resume session label). Omit for non-resume logins.
   */
  identity?: IdentityChipProps;
}

export default function PortalChooser({
  portals,
  onSelect,
  onBack,
  loading,
  error,
  filterable = false,
  identity,
}: PortalChooserProps) {
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    if (!filterable) return portals;
    const q = query.trim().toLowerCase();
    if (!q) return portals;
    // Portals are plain UID strings, so the filter matches on the uid.
    return portals.filter((portalUid) => portalUid.toLowerCase().includes(q));
  }, [portals, filterable, query]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="btn btn-link p-0 text-muted d-flex align-items-center gap-1"
          aria-label="Go back"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        {identity && (
          <IdentityChip
            label={identity.label}
            onLogout={identity.onLogout}
            onSwitchUser={identity.onSwitchUser}
          />
        )}
      </div>

      <h1 className="h3 fw-bold text-dark mb-1">Select your portal</h1>
      <p className="text-muted mb-4">
        Your account spans multiple portals. Choose which one to sign in to.
      </p>

      {error && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          {error}
        </div>
      )}

      {filterable && (
        <>
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Filter portals"
            aria-label="Filter portals"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <p className="text-muted small mb-3">
            {shown.length} of {portals.length}
          </p>
        </>
      )}

      {filterable && shown.length === 0 ? (
        <p className="text-muted">No portals match your search.</p>
      ) : (
        <div
          role="list"
          aria-label="Available portals"
          // When filterable (identity spans many portals), the list becomes its
          // own scroll region so the filter + count above stay pinned while only
          // the cards scroll. Capped to 60vh so the centred auth card never
          // grows past the viewport. Non-filterable callers keep today's
          // unbounded layout unchanged. ZC-5705 (mirrors CompanyChooser).
          className={filterable ? "overflow-auto" : undefined}
          style={filterable ? { maxHeight: "60vh" } : undefined}
        >
          {shown.map((portalUid) => (
            <div key={portalUid} role="listitem">
              <PortalCard
                portalUid={portalUid}
                onSelect={onSelect}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
